import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  ArrowUpToLine, CheckCheck, CircleCheck, CircleDashed, Compass, CornerDownRight,
  Fingerprint, Gem, GripVertical, Heart, History, Inbox, Pencil, Plus, RefreshCw,
  Redo2, Repeat, Sparkles, Star, Target, Trash2, Undo2, UserRound, X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { RootState } from '../../store'
import {
  materialize, materializableType, loadLedger, staleCommitted,
  loadOverrides, saveOverrides, loadCustom, saveCustom, customToItem,
  defaultFieldsOf, effFields, syncMaterialized, removeMaterialized,
  removeMaterializedByKey, loadHistory, saveHistory, HISTORY_MAX,
  type Ledger, type Overrides, type CustomDraft, type MaterializedType, type ItemOverride,
  type MaterializeDeps, type MapHistory, type HistoryEntry,
} from '../../services/lifemapMaterializer'
import type { Weekday, TimeWindow } from '../../services/habitService'
import {
  hortonV3Service,
  type LifeMapItem,
  type LifeMapEdge,
  type LifeMapResponse,
  type PendingResponse,
  type RegroupProposal,
} from '../../services/hortonV3Service'
import { BRAND } from '../../config/brand'

interface LifecoachLifeMapProps {
  /** Bumped by the parent after each turn to trigger a background refetch. */
  refreshSignal?: number
  /**
   * The server-owned LIFETIME spend, handed up on every successful fetch.
   *
   * The map is built by the consolidation plane a minute AFTER the reply, so
   * the turn response cannot know what it cost: the header seeded itself on
   * mount and from each turn, and therefore showed the conversation's cost
   * while omitting the run that produced the items on screen (measured
   * 2026-08-16: $0.002973 shown, $0.013794 more actually spent). This poll is
   * already asking the server every few seconds — it carries the answer.
   */
  onCost?: (usd: number) => void
}

// Only these relations nest the tree (child -> parent). `in_tension_with` is a
// flag, not a hierarchy edge — walking it would create false nesting.
const HIER = new Set(['serves', 'belongs_to'])

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

// ── The page-type ladder ──────────────────────────────────────────────────
//
// Dreams are the top of the ladder and NEVER have a parent. Goals live under
// dreams. Skills, habits and tasks live under dreams or goals (the create
// APIs carry parent_dream_id and parent_goal_id, nothing else). These rules
// bound every parent-choosing surface — the editor's pickers and drag & drop
// targets alike. The ENGINE's own tree is not re-shaped by them: an engine
// edge the ladder wouldn't draw still displays, and materialize()'s chain
// walk resolves it to the nearest dream/goal ancestor at create time.
const PARENT_TYPES: Record<MaterializedType, MaterializedType[]> = {
  dream: [],
  goal: ['dream'],
  skill: ['dream', 'goal'],
  habit: ['dream', 'goal'],
  task: ['dream', 'goal'],
}

/** Can a `parentType` item hold children at all? */
const canHoldChildren = (t: MaterializedType) => t === 'dream' || t === 'goal'

// One visual identity per page type: icon + tint. Used for the row glyph,
// the type pill and the section card's accent edge.
interface TypeMeta { Icon: LucideIcon; text: string; chip: string; edge: string; label: string }
const TYPE_META: Record<MaterializedType, TypeMeta> = {
  dream: { Icon: Star, text: 'text-amber-500', chip: 'bg-amber-500/10', edge: 'border-l-amber-500/50', label: 'dream' },
  goal: { Icon: Target, text: 'text-sky-500', chip: 'bg-sky-500/10', edge: 'border-l-sky-500/50', label: 'goal' },
  skill: { Icon: Gem, text: 'text-violet-500', chip: 'bg-violet-500/10', edge: 'border-l-violet-500/50', label: 'skill' },
  habit: { Icon: Repeat, text: 'text-emerald-500', chip: 'bg-emerald-500/10', edge: 'border-l-emerald-500/50', label: 'habit' },
  task: { Icon: CircleCheck, text: 'text-teal-500', chip: 'bg-teal-500/10', edge: 'border-l-teal-500/50', label: 'task' },
}

// Engine-only kinds (self-knowledge — they stay on the map, no page type).
const KIND_META: Record<string, { Icon: LucideIcon; text: string; chip: string }> = {
  value: { Icon: Heart, text: 'text-pink-500', chip: 'bg-pink-500/10' },
  identity_theme: { Icon: Fingerprint, text: 'text-pink-500', chip: 'bg-pink-500/10' },
  person_perception: { Icon: UserRound, text: 'text-yellow-500', chip: 'bg-yellow-500/10' },
}

/** The glyph a row wears: its target page type's identity; a direction keeps
 * the compass even though it materializes as a dream; engine-only kinds get
 * their own; anything else a dashed circle. */
function glyphFor(item: LifeMapItem, effType: MaterializedType | null): { Icon: LucideIcon; text: string; chip: string } {
  if (item.kind === 'direction') return { Icon: Compass, text: TYPE_META.dream.text, chip: TYPE_META.dream.chip }
  if (effType) return TYPE_META[effType]
  return KIND_META[item.kind] ?? { Icon: CircleDashed, text: 'text-muted', chip: 'bg-surface-alt' }
}

const TYPE_OPTIONS: MaterializedType[] = ['dream', 'goal', 'skill', 'habit', 'task']
/** Detail-page route segment per page type — /tasks/97, /dreams/12, … */
const TYPE_ROUTE: Record<MaterializedType, string> = {
  dream: 'dreams', goal: 'goals', skill: 'skills', habit: 'habits', task: 'tasks',
}
const PRIORITY_OPTIONS = ['low', 'medium', 'high'] as const
const CADENCE_OPTIONS = ['daily', 'weekly', 'biweekly', 'monthly'] as const
const WINDOW_OPTIONS = ['morning', 'afternoon', 'evening', 'night'] as const
const WEEKDAYS: Array<{ key: Weekday; label: string }> = [
  { key: 'monday', label: 'mon' }, { key: 'tuesday', label: 'tue' },
  { key: 'wednesday', label: 'wed' }, { key: 'thursday', label: 'thu' },
  { key: 'friday', label: 'fri' }, { key: 'saturday', label: 'sat' },
  { key: 'sunday', label: 'sun' },
]
/** The per-field overrides the editor manages (title/type/parent aside). */
const FIELD_KEYS = ['description', 'priority', 'area', 'cadence', 'days', 'window', 'at', 'duration', 'due'] as const

// ── Review baseline: "show me only what changed since I last looked" ──────
//
// The engine has a run-cursor delta endpoint (sidecar POST /v2/lifemap/delta)
// but it is not relayed through newage/saas yet. Until it is, the panel
// derives the same answer client-side: a reviewed SNAPSHOT of the displayed
// fields per item id, stored per user, diffed against every fresh fetch.
// Deterministic over exactly what the panel can show — a field this panel
// doesn't render can't be missed in review either.

const FP_SEP = '\u0001'

/** Labeled fingerprint of the fields this panel displays. Confidence is
 * quantized to 0.1 so consolidation's small drifts don't spam the diff. */
function fingerprintParts(i: LifeMapItem): Record<string, string> {
  return {
    title: i.display_title || i.title || '',
    kind: i.kind || '',
    status: i.status || '',
    area: (i.life_areas && i.life_areas.length ? [...i.life_areas].sort().join(',') : i.life_area) || '',
    supports: i.supports || '',
    schedule: i.schedule?.text || '',
    cadence: i.cadence || '',
    summary: i.summary || '',
    confidence: String(Math.round((i.confidence || 0) * 10) / 10),
    flags: [i.needs_when && 'needs_when', i.ad_hoc && 'ad_hoc', i.supports_nothing && 'supports_nothing']
      .filter(Boolean).join(','),
  }
}

function fingerprint(i: LifeMapItem): string {
  const p = fingerprintParts(i)
  return Object.keys(p).sort().map((k) => `${k}=${p[k]}`).join(FP_SEP)
}

/** Which labeled parts differ between two fingerprints — "status, schedule". */
function changedFields(prevFp: string, item: LifeMapItem): string[] {
  const prev = new Map(prevFp.split(FP_SEP).map((kv) => {
    const i = kv.indexOf('='); return [kv.slice(0, i), kv.slice(i + 1)] as const
  }))
  const cur = fingerprintParts(item)
  return Object.keys(cur).filter((k) => (prev.get(k) ?? '') !== cur[k])
}

interface ReviewBaseline {
  reviewedAt: string
  /** id -> { f: fingerprint, t: title, p: parent engine id (null = was a
   * root; absent on baselines from before parents were recorded), k: kind }.
   * The parent is what lets a removal be shown WHERE it used to sit. */
  items: Record<string, { f: string; t: string; p?: number | null; k?: string }>
}

const reviewKey = (userId: string) => `${BRAND.assistant}-lifemap-reviewed:${userId}`

function loadBaseline(userId: string): ReviewBaseline | null {
  try {
    const raw = localStorage.getItem(reviewKey(userId))
    if (!raw) return null
    const b = JSON.parse(raw) as ReviewBaseline
    return b && b.items ? b : null
  } catch { return null }
}

interface TreeIndex {
  byId: Map<number, LifeMapItem>
  parentOf: Map<number, { parent: number; relation: string }>
  childrenOf: Map<number, number[]>
  tension: Set<number>
  extraParents: Map<number, number>
  roots: LifeMapItem[]
}

function buildTree(map: LifeMapItem[], edges: LifeMapEdge[]): TreeIndex {
  const byId = new Map(map.map((h) => [h.id, h]))
  const parentOf = new Map<number, { parent: number; relation: string }>()
  const childrenOf = new Map<number, number[]>()
  const tension = new Set<number>()
  const extraParents = new Map<number, number>()

  for (const e of edges || []) {
    if (e.kind === 'in_tension_with') { tension.add(e.src); tension.add(e.dst); continue }
    if (!HIER.has(e.kind)) continue
    if (!byId.has(e.src) || !byId.has(e.dst)) continue
    if (parentOf.has(e.src)) {
      extraParents.set(e.src, (extraParents.get(e.src) || 0) + 1)
      continue
    }
    parentOf.set(e.src, { parent: e.dst, relation: e.kind })
    if (!childrenOf.has(e.dst)) childrenOf.set(e.dst, [])
    childrenOf.get(e.dst)!.push(e.src)
  }
  const roots = map.filter((h) => !parentOf.has(h.id))
  // Every item has at most ONE parent here, so a walk from the roots reaches
  // each item at most once — no per-render visited set is needed (a Set
  // mutated during render broke under StrictMode's double render). The one
  // shape that walk cannot reach is a cycle (a→b→a): nothing in it is a
  // root. The engine's guards forbid it; if it ever appears, its members are
  // surfaced as roots rather than vanishing from the map.
  const reachable = new Set<number>()
  const stack = roots.map((r) => r.id)
  while (stack.length) {
    const id = stack.pop()!
    if (reachable.has(id)) continue
    reachable.add(id)
    for (const k of childrenOf.get(id) || []) stack.push(k)
  }
  for (const h of map) if (!reachable.has(h.id)) roots.push(h)
  return { byId, parentOf, childrenOf, tension, extraParents, roots }
}

function WhenWhere({ item }: { item: LifeMapItem }) {
  const chips: string[] = []
  const T = (item.temporal || {}) as Record<string, any>
  // Per-day times ("mon 7pm, tue 3pm, sat 8pm") are ONE commitment with
  // several sessions: a chip each, in calendar order. Rendering only `days`
  // and a single `at` here would show one hour for all of them — the engine
  // stops storing `at` once the hours differ, so it would show none at all.
  const slots: Array<{ day?: string; at?: string | null }> =
    Array.isArray(T.slots) ? T.slots : []
  const perDayTimes = new Set(slots.map((s) => s?.at).filter(Boolean))
  if (slots.length && perDayTimes.size > 1) {
    const byDay = new Map(slots.filter((s) => s?.day).map((s) => [s.day as string, s.at]))
    for (const d of DAY_ORDER) {
      if (byDay.has(d)) chips.push(byDay.get(d) ? `${d} ${byDay.get(d)}` : d)
    }
  } else {
    if (Array.isArray(T.days) && T.days.length) {
      chips.push(DAY_ORDER.filter((d) => T.days.includes(d)).join(' '))
    }
    if (T.at) chips.push(String(T.at))
    else if (T.part_of_day) chips.push(String(T.part_of_day))
  }
  if (T.duration_minutes) chips.push(`${T.duration_minutes} min`)
  if (T.recurrence && T.recurrence !== 'unstated') chips.push(String(T.recurrence))
  if (T.deadline) chips.push(`by ${T.deadline}`)
  else if (T.horizon_months) chips.push(`within ${T.horizon_months} mo`)
  if (item.location) chips.push(`◎ ${item.location}`)
  // The engine will not invent a time (law 5). This is the ask, not a guess.
  if (item.needs_when) chips.push('when?')
  // The compiled calendar rule(s) — what a scheduler would run. Shown on
  // hover so the chips stay readable and the rule is one click from a diff.
  const rrules: string[] = Array.isArray(item.schedule?.rrules) ? item.schedule!.rrules : []
  if (!chips.length && !rrules.length) return null
  return (
    <div className="flex flex-wrap gap-1 mt-1" title={rrules.length ? rrules.join('\n') : undefined}>
      {chips.map((c, i) => (
        <span key={i} className="text-app-nano font-mono px-1.5 py-px rounded-md bg-surface-alt/70 border border-soft/70 text-muted">
          {c}
        </span>
      ))}
      {rrules.length > 0 && (
        <span className="text-app-nano font-mono px-1.5 py-px rounded-md border border-dashed border-soft text-muted opacity-70">
          {rrules.length === 1 ? 'RRULE' : `${rrules.length} RRULEs`}
        </span>
      )}
    </div>
  )
}

type AddState = 'none' | 'adding' | 'added' | 'unsupported'

type DiffBadge = { kind: 'new' } | { kind: 'changed'; fields: string[] }

type ParentSel = 'engine' | 'top' | number

/** Everything the editor form holds; committed as one save. */
interface EditorDraft {
  title: string
  type: MaterializedType
  description: string
  priority: 'low' | 'medium' | 'high'
  area: string
  cadence: string
  days: Weekday[]
  window: string
  at: string
  duration: string
  due: string
  parent: ParentSel
  /** Items to pull under this one on save. */
  children: number[]
}

/** A removed item, shown ghosted where it used to sit. */
interface Ghost { id: string; title: string }

/**
 * Everything a row needs, bundled once — the same renderer serves the full
 * tree and the changes-in-place view (which only adds gating + badges).
 */
interface RowCtx {
  tree: TreeIndex
  addStateOf: (item: LifeMapItem) => AddState
  onAdd: (item: LifeMapItem) => void
  titleOf: (i: LifeMapItem) => string
  typeOf: (i: LifeMapItem) => MaterializedType | null
  isExcluded: (id: number) => boolean
  isPlaced: (id: number) => boolean
  isSelected: (id: number) => boolean
  toggleSelected: (id: number) => void
  /** Retype an item. Converting to `dream` also moves it to the top level;
   * a user placement that no longer fits the new type reverts to the engine. */
  setType: (item: LifeMapItem, t: MaterializedType) => void
  exclude: (id: number) => void
  restore: (id: number) => void
  clearPlacement: (id: number) => void
  removeCustom: (id: number) => void
  /** Committed items only: delete the page item (confirmed), row returns to
   * being a proposal. */
  removeFromPages: (item: LifeMapItem) => void
  /** Double-click: a committed row opens its page (/tasks/97 for the task
   * with saasId 97); a pending row opens the inline editor. */
  openItemPage: (item: LifeMapItem) => void
  editingId: number | null
  openEditor: (id: number) => void
  closeEditor: () => void
  saveEditor: (item: LifeMapItem, draft: EditorDraft, initParent: ParentSel) => void
  overrideOf: (id: number) => ItemOverride | undefined
  /** What the parent select should open on: the engine's edge, top level,
   * or the item the user already placed it under. */
  initialParentSel: (id: number) => ParentSel
  /** Title of the ENGINE's parent for this item, for the select's label. */
  enginePlacementName: (id: number) => string | null
  /** Valid parents for an item IF it were the given type — the ladder rules
   * live here: goals list only dreams; leaves list dreams and goals. */
  eligibleParentsFor: (id: number, childType: MaterializedType) => LifeMapItem[]
  /** Valid children an item of the given type could hold. */
  eligibleChildrenFor: (id: number, parentType: MaterializedType) => LifeMapItem[]
  drag: {
    dragId: number | null
    start: (id: number) => void
    end: () => void
    overId: number | null
    setOver: (id: number | null) => void
    canDropOn: (id: number) => boolean
    dropOn: (id: number) => void
  }
  /** Present when a review baseline exists: what changed since it. */
  diffOf?: (id: number) => DiffBadge | null
  /** Changes view: render only these ids (changed nodes + their ancestors). */
  visibleIds?: Set<number> | null
  /** Changes view: removed items to ghost under this (still-live) parent. */
  ghostsUnder?: (id: number) => Ghost[]
  /** Changes view: dim rows that are only shown as ancestors for context. */
  dimContext?: boolean
}

// Shared control styles.
const LBL = 'text-app-nano font-semibold uppercase tracking-wider text-muted mb-0.5'
const INP = 'text-xs px-2 py-1.5 rounded-lg border border-soft bg-surface text-primary focus:outline-none focus:border-brand/50'
const SEG_ON = 'bg-surface text-primary shadow-sm border border-soft'
const SEG_OFF = 'text-muted hover:text-primary border border-transparent'

/**
 * The full editor for one proposal item: every field the target page type
 * will be created with, plus its place in the ladder. Dreams get no parent
 * picker at all (they ARE the top); goals may only sit under dreams; leaves
 * under dreams or goals. Committed as ONE save.
 */
function ItemEditor({ item, ctx }: { item: LifeMapItem; ctx: RowCtx }) {
  const isCustom = item.id < 0
  const [initParent] = useState<ParentSel>(() => ctx.initialParentSel(item.id))
  const [d, setD] = useState<EditorDraft>(() => {
    const F = effFields(item, ctx.overrideOf(item.id))
    const type = ctx.typeOf(item) ?? 'task'
    return {
      title: ctx.titleOf(item),
      type,
      description: F.description ?? '',
      priority: F.priority,
      area: F.area ?? '',
      cadence: F.cadence ?? '',
      days: F.days ?? [],
      window: F.window ?? '',
      at: F.at ?? '',
      duration: F.duration != null ? String(F.duration) : '',
      due: F.due ?? '',
      parent: type === 'dream' ? 'top' : ctx.initialParentSel(item.id),
      children: [],
    }
  })
  const set = (patch: Partial<EditorDraft>) => setD((p) => ({ ...p, ...patch }))

  // Retype inside the editor re-applies the ladder to the draft: a dream
  // loses its parent (it IS the top); an invalid chosen parent falls back;
  // chosen children that the new type cannot hold are dropped.
  const onType = (t: MaterializedType) => {
    const okChildren = new Set(ctx.eligibleChildrenFor(item.id, t).map((c) => c.id))
    const children = d.children.filter((cid) => okChildren.has(cid))
    if (t === 'dream') { set({ type: t, parent: 'top', children }); return }
    let parent = d.parent
    if (typeof parent === 'number' && !ctx.eligibleParentsFor(item.id, t).some((p) => p.id === parent)) {
      parent = isCustom ? 'top' : 'engine'
    }
    set({ type: t, parent, children })
  }

  const parents = ctx.eligibleParentsFor(item.id, d.type)
  const childCands = ctx.eligibleChildrenFor(item.id, d.type).filter((c) => !d.children.includes(c.id))
  const engineName = ctx.enginePlacementName(item.id)

  return (
    <div className="mt-2 mb-1 rounded-xl border border-brand/25 bg-surface shadow-md p-3 space-y-2.5 max-w-xl"
         onClick={(e) => e.stopPropagation()}>
      <div>
        <div className={LBL}>title</div>
        <input
          autoFocus
          value={d.title}
          onChange={(e) => set({ title: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Escape') ctx.closeEditor() }}
          className={`${INP} w-full font-medium`}
        />
      </div>
      <div>
        <div className={LBL}>description</div>
        <textarea value={d.description} onChange={(e) => set({ description: e.target.value })}
                  rows={2} className={`${INP} w-full resize-y`} />
      </div>
      <div>
        <div className={LBL}>type</div>
        <div className="inline-flex rounded-lg bg-surface-alt/80 p-0.5 gap-0.5 flex-wrap">
          {TYPE_OPTIONS.map((t) => {
            const M = TYPE_META[t]
            const on = d.type === t
            return (
              <button key={t} type="button" onClick={() => onType(t)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-app-micro transition-colors ${on ? SEG_ON : SEG_OFF}`}>
                <M.Icon className={`w-3 h-3 ${M.text}`} /> {M.label}
              </button>
            )
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className={LBL}>priority</div>
          <div className="inline-flex rounded-lg bg-surface-alt/80 p-0.5 gap-0.5">
            {PRIORITY_OPTIONS.map((p) => (
              <button key={p} type="button" onClick={() => set({ priority: p })}
                      className={`px-2 py-1 rounded-md text-app-micro transition-colors ${d.priority === p ? SEG_ON : SEG_OFF}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <label>
          <div className={LBL}>area</div>
          <input value={d.area} onChange={(e) => set({ area: e.target.value })}
                 placeholder="health, career, …" className={`${INP} w-32`} />
        </label>
      </div>
      {d.type === 'habit' && (
        <div className="flex flex-wrap items-end gap-3">
          <label>
            <div className={LBL}>cadence</div>
            <select value={d.cadence} onChange={(e) => set({ cadence: e.target.value })} className={INP}>
              <option value="">—</option>
              {CADENCE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div>
            <div className={LBL}>days</div>
            <div className="flex gap-0.5">
              {WEEKDAYS.map(({ key, label }) => (
                <button key={key} type="button"
                        onClick={() => set({ days: d.days.includes(key) ? d.days.filter((x) => x !== key) : [...d.days, key] })}
                        className={`text-app-nano px-1.5 py-1 rounded-md border transition-colors ${d.days.includes(key)
                          ? 'border-emerald-500/50 text-emerald-500 bg-emerald-500/10'
                          : 'border-soft text-muted hover:text-primary'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label>
            <div className={LBL}>window</div>
            <select value={d.window} onChange={(e) => set({ window: e.target.value })} className={INP}>
              <option value="">—</option>
              {WINDOW_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </label>
          <label>
            <div className={LBL}>start time</div>
            <input type="time" value={d.at}
                   onChange={(e) => set({ at: e.target.value })} className={INP}
                   title={`Habits need a start time to appear on Today and reach ${BRAND.assistant}`} />
          </label>
          <label>
            <div className={LBL}>minutes</div>
            <input type="number" min="0" value={d.duration}
                   onChange={(e) => set({ duration: e.target.value })} className={`${INP} w-16`} />
          </label>
        </div>
      )}
      {d.type === 'task' && (
        <label className="block">
          <div className={LBL}>due date</div>
          <input type="date" value={d.due} onChange={(e) => set({ due: e.target.value })} className={INP} />
        </label>
      )}
      {d.type === 'dream' ? (
        <div className="flex items-center gap-1.5 text-app-micro text-muted">
          <Star className="w-3 h-3 text-amber-500" />
          Dreams sit at the top level — nothing holds a dream.
        </div>
      ) : (
        <div>
          <div className={LBL}>
            parent — {d.type === 'goal' ? 'a goal lives under a dream' : `a ${d.type} lives under a dream or a goal`}
          </div>
          <select
            value={typeof d.parent === 'number' ? String(d.parent) : d.parent}
            onChange={(e) => {
              const v = e.target.value
              set({ parent: v === 'engine' || v === 'top' ? v : Number(v) })
            }}
            className={`${INP} w-full`}
          >
            {!isCustom && (
              <option value="engine">
                engine’s placement{engineName ? ` (under ${engineName})` : ' (top level)'}
              </option>
            )}
            <option value="top">top level</option>
            {parents.map((p) => (
              <option key={p.id} value={String(p.id)}>under: {ctx.titleOf(p)} · {ctx.typeOf(p) ?? p.kind}</option>
            ))}
          </select>
        </div>
      )}
      {canHoldChildren(d.type) && (
        <div>
          <div className={LBL}>
            children — pull items under this {d.type}
          </div>
          {d.children.length > 0 && (
            <div className="flex flex-wrap gap-1 my-1">
              {d.children.map((cid) => {
                const c = ctx.tree.byId.get(cid)
                return (
                  <span key={cid} className="text-app-nano px-2 py-0.5 rounded-full border border-brand/40 text-brand inline-flex items-center gap-1">
                    {c ? ctx.titleOf(c) : `#${cid}`}
                    <button type="button" onClick={() => set({ children: d.children.filter((x) => x !== cid) })}
                            className="hover:text-bad" title="Don’t pull this one under">×</button>
                  </span>
                )
              })}
            </div>
          )}
          {childCands.length > 0 ? (
            <select
              value=""
              onChange={(e) => { const v = Number(e.target.value); if (v) set({ children: [...d.children, v] }) }}
              className={`${INP} w-full`}
            >
              <option value="">choose an item to become a child…</option>
              {childCands.map((c) => (
                <option key={c.id} value={String(c.id)}>{ctx.titleOf(c)} · {ctx.typeOf(c) ?? c.kind}</option>
              ))}
            </select>
          ) : d.children.length === 0 ? (
            <div className="text-app-nano text-muted">nothing eligible to pull under right now</div>
          ) : null}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={ctx.closeEditor}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-soft text-muted hover:text-primary">Cancel</button>
        <button onClick={() => ctx.saveEditor(item, d, initParent)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand text-white hover:bg-brand-hover shadow-sm">Save</button>
      </div>
    </div>
  )
}

/** Build the floating pill that follows the cursor during a drag. */
function makeDragGhost(title: string): HTMLElement {
  const g = document.createElement('div')
  g.textContent = title
  g.style.cssText = [
    'position:fixed', 'top:-1000px', 'left:-1000px', 'z-index:9999',
    'padding:4px 12px', 'border-radius:9999px',
    'background:var(--color-brand,#6366f1)', 'color:#fff',
    'font-size:12px', 'font-weight:600', 'max-width:260px',
    'white-space:nowrap', 'overflow:hidden', 'text-overflow:ellipsis',
    'box-shadow:0 6px 16px rgba(0,0,0,.25)',
  ].join(';')
  document.body.appendChild(g)
  return g
}

function Node({ item, depth, ctx }: { item: LifeMapItem; depth: number; ctx: RowCtx }) {
  if (depth > 6) return null
  if (ctx.visibleIds && !ctx.visibleIds.has(item.id)) return null
  const kids = (ctx.tree.childrenOf.get(item.id) || [])
    .map((k) => ctx.tree.byId.get(k)!)
    .filter(Boolean)
    .sort((a, b) => b.confidence - a.confidence || a.id - b.id)
  const extra = ctx.tree.extraParents.get(item.id)
  const st = ctx.addStateOf(item)
  const excluded = ctx.isExcluded(item.id)
  const pending = st === 'none' && !excluded
  const committed = st === 'added' && !excluded
  // Committed items stay first-class: still draggable, editable, retypable —
  // their page item follows every change.
  const interactive = pending || committed
  const editing = ctx.editingId === item.id
  const badge = ctx.diffOf?.(item.id) ?? null
  const isCustom = item.id < 0
  const ghosts = ctx.ghostsUnder?.(item.id) ?? []
  const effTitle = ctx.titleOf(item)
  const effType = ctx.typeOf(item)
  const G = glyphFor(item, effType)

  const dragging = ctx.drag.dragId != null
  const isDragSelf = ctx.drag.dragId === item.id
  const dropReady = dragging && !isDragSelf && ctx.drag.canDropOn(item.id)
  const isOver = dropReady && ctx.drag.overId === item.id
  // While a drag is live, everything that cannot receive it steps back —
  // the un-dimmed rows ARE the answer to "where can this go?".
  const dimForDrag = dragging && !dropReady && !isDragSelf
  const dimContext = Boolean(ctx.dimContext && !badge && !excluded)

  const glyphTip = [
    item.kind,
    item.cadence ? `cadence ${item.cadence}` : null,
    typeof item.evidence === 'number' ? `${item.evidence} evidence` : null,
    `confidence ${(item.confidence ?? 0).toFixed?.(2) ?? item.confidence}`,
    item.supports ? `serves ${item.supports}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className={depth > 0 ? 'ml-[13px] pl-2.5 border-l-2 border-soft/60' : ''}>
      <div
        draggable={interactive && !editing}
        onDragStart={(e) => {
          // Grab anywhere on the row — except its interactive controls.
          if ((e.target as HTMLElement).closest('button,select,input,textarea,a')) {
            e.preventDefault()
            return
          }
          e.stopPropagation()
          e.dataTransfer.setData('text/plain', String(item.id))
          e.dataTransfer.effectAllowed = 'move'
          const ghost = makeDragGhost(effTitle)
          e.dataTransfer.setDragImage(ghost, 14, 14)
          window.setTimeout(() => ghost.remove(), 0)
          ctx.drag.start(item.id)
        }}
        onDragEnd={() => ctx.drag.end()}
        onDoubleClick={(e) => {
          if ((e.target as HTMLElement).closest('button,select,input,textarea,a')) return
          e.stopPropagation()
          ctx.openItemPage(item)
        }}
        onDragOver={(e) => { if (dropReady) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; ctx.drag.setOver(item.id) } }}
        onDragLeave={() => { if (isOver) ctx.drag.setOver(null) }}
        onDrop={(e) => { if (dropReady) { e.preventDefault(); e.stopPropagation(); ctx.drag.dropOn(item.id) } }}
        className={[
          'group relative rounded-lg px-1.5 py-1.5 transition-all duration-150',
          interactive && !editing ? 'cursor-grab active:cursor-grabbing' : '',
          isOver ? 'ring-2 ring-brand bg-brand-subtle/50' : '',
          isDragSelf ? 'opacity-40 ring-1 ring-dashed ring-brand/40' : '',
          dimForDrag || dimContext ? 'opacity-40' : '',
          !dragging && !editing ? 'hover:bg-surface-alt/50' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="flex items-start gap-1.5">
          {interactive && (
            <span
              className="self-stretch flex items-center text-muted/30 group-hover:text-muted/70 transition-colors -ml-0.5"
              title="Drag anywhere on the row to move it — onto a dream or goal, or the zones below"
            >
              <GripVertical className="w-3.5 h-3.5" />
            </span>
          )}
          {pending && (
            <input
              type="checkbox"
              className="mt-1 w-3.5 h-3.5 accent-[var(--color-brand)] cursor-pointer"
              checked={ctx.isSelected(item.id)}
              onChange={() => ctx.toggleSelected(item.id)}
              onClick={(e) => e.stopPropagation()}
              title="Queued — one button below adds everything checked"
            />
          )}
          <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${G.chip} ${G.text}`}
                title={glyphTip}>
            {isOver ? <CornerDownRight className="w-3 h-3" /> : <G.Icon className="w-3 h-3" />}
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className={`text-sm ${depth === 0 ? 'font-semibold text-[15px]' : 'font-medium'} ${excluded ? 'line-through text-muted' : 'text-primary'}`}
                title={item.display_title && item.display_title !== item.title ? `in their words: “${item.title}”` : undefined}
              >
                {effTitle}
              </span>
              {badge?.kind === 'new' && (
                <span className="text-app-nano font-semibold px-1.5 py-px rounded-full bg-emerald-500/10 text-emerald-500 uppercase tracking-wide">new</span>
              )}
              {badge?.kind === 'changed' && (
                <span className="text-app-nano font-semibold px-1.5 py-px rounded-full bg-amber-500/10 text-warn" title="Changed since your last review">
                  ↻ {badge.fields.join(', ') || 'details'}
                </span>
              )}
              {!excluded && !isCustom && item.status && item.status !== 'active' && (
                <span className="text-app-nano font-semibold px-1.5 py-px rounded-full bg-amber-500/10 text-warn uppercase tracking-wide">
                  {item.status}
                </span>
              )}
              {ctx.isPlaced(item.id) && !excluded && (
                <button
                  onClick={(e) => { e.stopPropagation(); ctx.clearPlacement(item.id) }}
                  className="text-app-nano px-1.5 py-px rounded-full bg-brand-subtle/60 text-brand hover:underline"
                  title="You placed this here (drag & drop or the editor). Click to give it back to the engine's placement."
                >
                  placed by you ↺
                </button>
              )}
              {!excluded && (() => {
                const ovRow = ctx.overrideOf(item.id)
                const edited = ovRow ? FIELD_KEYS.filter((k) => ovRow[k] !== undefined) : []
                return edited.length > 0 ? (
                  <span className="text-app-nano px-1.5 py-px rounded-full bg-brand-subtle/60 text-brand"
                        title={`Fields you edited: ${edited.join(', ')}. Open the editor to change or revert them.`}>
                    ✎ edited
                  </span>
                ) : null
              })()}
              {item.area_locked && (
                <span className="text-app-nano text-brand" title="You placed this area; consolidation will not move it back">✎</span>
              )}
              {ctx.tree.tension.has(item.id) && (
                <span className="text-app-nano font-semibold px-1.5 py-px rounded-full bg-red-500/10 text-bad">⚠ in tension</span>
              )}
              {item.supports_nothing && !excluded && (
                <span className="text-app-nano px-1.5 py-px rounded-full border border-bad/30 text-bad" title="A means with no objective drifts out of relevance">
                  supports nothing yet
                </span>
              )}
              {extra ? (
                <span className="text-app-nano text-muted">+{extra} other parent{extra > 1 ? 's' : ''}</span>
              ) : null}

              <span className="ml-auto pl-2 flex items-center gap-1">
                {excluded ? (
                  <button onClick={() => ctx.restore(item.id)} className="text-app-nano underline text-muted hover:text-brand whitespace-nowrap" title="Put it back in the queue">
                    left off — undo
                  </button>
                ) : st === 'adding' ? (
                  <span className="text-app-nano text-muted whitespace-nowrap animate-pulse">syncing…</span>
                ) : interactive && !editing ? (
                  <>
                    {committed && (
                      <span className="text-app-nano text-ok whitespace-nowrap inline-flex items-center gap-1"
                            title="Created on your site pages — every edit here updates the page item too. Double-click the row to open its page.">
                        <CircleCheck className="w-3 h-3" /> on pages
                      </span>
                    )}
                    <select
                      value={effType ?? 'task'}
                      onChange={(e) => ctx.setType(item, e.target.value as MaterializedType)}
                      onClick={(e) => e.stopPropagation()}
                      className={`text-app-nano px-1.5 py-0.5 rounded-full border border-soft bg-surface cursor-pointer ${G.text}`}
                      title={committed
                        ? 'The page type. Changing it recreates the page item as the new type.'
                        : item.kind === 'habit_candidate'
                          ? (effType === 'task'
                            ? 'You never said this repeats, so it defaults to a one-off task — switch it to habit if it should'
                            : 'A schedule was stated, so this defaults to a habit')
                          : 'The page type this will be created as. Dreams move to the top level.'}
                    >
                      {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                      <button onClick={() => ctx.openEditor(item.id)} className="p-1 rounded-md text-muted hover:text-brand hover:bg-surface-alt"
                              title="Edit everything: fields, type, parent, children">
                        <Pencil className="w-3 h-3" />
                      </button>
                      {pending ? (
                        <>
                          <button
                            onClick={() => (isCustom ? ctx.removeCustom(item.id) : ctx.exclude(item.id))}
                            className="p-1 rounded-md text-muted hover:text-bad hover:bg-surface-alt"
                            title={isCustom ? 'Delete this draft of yours' : 'Leave this off your pages (undoable)'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); ctx.onAdd(item) }}
                            className="text-app-nano px-1.5 py-0.5 rounded-full border border-brand/40 text-brand hover:bg-brand-subtle whitespace-nowrap"
                            title="Add just this one now (parents included)"
                          >
                            + now
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => ctx.removeFromPages(item)}
                          className="p-1 rounded-md text-muted hover:text-bad hover:bg-surface-alt"
                          title="Remove from your pages — deletes the page item; it stays on the map as a proposal"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  </>
                ) : null}
              </span>
            </div>
            {!excluded && (
              <>
                {item.summary && <div className="text-xs text-secondary mt-0.5 leading-snug">{item.summary}</div>}
                {item.rationale && (
                  <div className="text-xs text-brand/80 mt-0.5 pl-2 border-l-2 border-brand/30">why: {item.rationale}</div>
                )}
                <WhenWhere item={item} />
                {(item.life_areas && item.life_areas.length ? item.life_areas : item.life_area ? [item.life_area] : []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(item.life_areas && item.life_areas.length ? item.life_areas : item.life_area ? [item.life_area] : []).map((a) => (
                      <span key={a} className="text-app-nano px-1.5 py-px rounded-full border border-soft/70 text-muted" title="life area (tag)">
                        {a}
                      </span>
                    ))}
                    {isCustom && <span className="text-app-nano text-muted">added by you</span>}
                  </div>
                )}
                {isCustom && !(item.life_areas?.length || item.life_area) && (
                  <div className="mt-0.5 text-app-nano text-muted">added by you</div>
                )}
              </>
            )}
            {editing && <ItemEditor key={item.id} item={item} ctx={ctx} />}
          </div>
        </div>
      </div>
      {kids.map((k) => (
        <Node key={k.id} item={k} depth={depth + 1} ctx={ctx} />
      ))}
      {ghosts.map((g) => (
        <div key={g.id} className="ml-[13px] pl-2.5 border-l-2 border-soft/60">
          <div className="py-1 px-1.5 flex items-center gap-1.5 rounded-lg border border-dashed border-soft/70 my-0.5">
            <span className="text-sm text-muted line-through">{g.title}</span>
            <span className="text-app-nano font-semibold px-1.5 py-px rounded-full bg-surface-alt text-muted uppercase tracking-wide">removed</span>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * The "being organized" strip. Compass replies instantly but builds the map on
 * a consolidation pass, so a short message's items appear minutes later. This
 * shows what was HEARD in the meantime — the raw observations, a pure
 * projection — so the person sees Compass took it in, and the "Analyze my map"
 * button runs that consolidation on demand instead of waiting on the timer.
 * Hidden when there is nothing pending and nothing to say.
 */
function PendingStrip({ pending, busy, message, onAnalyze }: {
  pending: PendingResponse | null
  busy: boolean
  message: string | null
  onAnalyze: () => void
}) {
  const obs = pending?.pending ?? []
  const canAnalyze = Boolean(pending?.can_analyze)
  if (obs.length === 0 && !message && !busy) return null
  return (
    <div className="mb-3 rounded-xl border border-dashed border-soft bg-surface-alt/30 p-2.5 text-xs">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-app-nano font-semibold uppercase tracking-wider text-muted">Being organized</span>
        {obs.length > 0 && <span className="text-muted">{obs.length} just heard</span>}
        <button onClick={onAnalyze} disabled={busy || (!canAnalyze && !busy)}
                title={`Run ${BRAND.assistant}’s analysis now instead of waiting for the automatic pass. It takes a few seconds.`}
                className="ml-auto text-xs px-2.5 py-1 rounded-lg bg-brand text-white hover:bg-brand-hover disabled:opacity-40">
          {busy ? 'Analyzing…' : 'Analyze my map'}
        </button>
      </div>
      {obs.length > 0 && (
        <ul className="space-y-0.5 text-secondary">
          {obs.map((o) => (
            <li key={o.observation_id} className={o.sensitive ? 'italic text-muted' : ''}>
              · {o.gist}
              {o.kind && <span className="text-muted"> ({o.kind.replace(/_/g, ' ')})</span>}
              {/* Heard, but on a different footing. Something written in the
                  diary only reaches the map once it recurs across days, so
                  Analyze will not place it today — say that here rather than
                  let the button look broken. */}
              {o.source === 'diary' && (
                <span className="text-muted"
                      title={`From your diary. ${BRAND.assistant} keeps it, and puts it on the map only if you write about it again another day.`}>
                  {' '}· from your diary
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {message && <div className="text-muted mt-1">{message}</div>}
    </div>
  )
}

/**
 * #42 — the regroup, in the panel. One button drafts a proposal (one whole-map
 * model call, charged like any other); the proposal is shown as WHAT WOULD
 * CHANGE — adds, removals WITH THEIR REASON (silence keeps, so every removal
 * carries an argument), what the guards refused — and two buttons decide it.
 * `no_change` is shown as itself: the map had nothing to move.
 */
function RegroupPanel({ pending, busy, message, onPropose, onDecide, tree }: {
  pending: RegroupProposal | null
  busy: boolean
  message: string | null
  onPropose: () => void
  onDecide: (id: number, d: 'accept' | 'reject') => void
  tree: TreeIndex | null
}) {
  const nameOf = (id: number) => {
    const it = tree?.byId.get(id)
    return it ? (it.display_title || it.title) : `#${id}`
  }
  const p = pending?.payload
  const s = p?.summary ?? {}
  return (
    <div className="mt-4 pt-3 border-t border-soft">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-app-nano font-semibold uppercase tracking-wider text-muted">Regroup</div>
        {!pending && (
          <button onClick={onPropose} disabled={busy}
                  className="ml-auto text-xs px-2.5 py-1 rounded-lg border border-soft hover:bg-surface-alt disabled:opacity-50"
                  title="One pass over the whole map: propose a better tree. Nothing moves until you say so.">
            {busy ? 'thinking…' : 'Propose a regroup'}
          </button>
        )}
      </div>
      {message && <div className="text-xs text-muted mb-1">{message}</div>}
      {pending && p && (
        <div className="text-xs text-secondary space-y-1">
          <div className="text-muted">
            +{s.edges_add ?? 0} attach · −{s.edges_remove ?? 0} remove · {s.edges_kept ?? 0} kept
            {s.retires ? ` · ${s.retires} retire` : ''}
            {s.orphans_with_candidate_parents ? ` · ${s.orphans_with_candidate_parents} still orphaned` : ''}
          </div>
          {(p.edges_add ?? []).map((e, i) => (
            <div key={`a-${i}`}>+ <strong>{nameOf(e.src)}</strong> → {nameOf(e.dst)} <span className="text-muted">({e.kind})</span></div>
          ))}
          {(p.edges_remove ?? []).map((e, i) => (
            <div key={`r-${i}`}>− <strong>{nameOf(e.src)}</strong> ↛ {nameOf(e.dst)}
              <span className="text-muted"> — {e.reason}{e.detail ? `: ${e.detail}` : ''}</span></div>
          ))}
          {(p.defects ?? []).length > 0 && (
            <div className="text-muted">refused by the guards: {(p.defects ?? []).map((d) => d.guard).join(', ')}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={() => onDecide(pending.proposal_id, 'accept')} disabled={busy}
                    className="text-xs px-2.5 py-1 rounded-lg bg-brand text-white hover:bg-brand-hover disabled:opacity-50">Accept</button>
            <button onClick={() => onDecide(pending.proposal_id, 'reject')} disabled={busy}
                    className="text-xs px-2.5 py-1 rounded-lg border border-soft hover:bg-surface-alt disabled:opacity-50">Reject</button>
            <span className="text-muted self-center">expires {new Date(pending.expires_at).toLocaleDateString()}</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** "+ add your own" — a user-authored draft that joins the proposal queue.
 * It starts at top level; drag it under a parent to place it. */
function AddYourOwn({ onAdd }: { onAdd: (title: string, type: MaterializedType) => void }) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<MaterializedType>('task')
  const submit = () => {
    const t = title.trim()
    if (!t) return
    onAdd(t, type)
    setTitle('')
    setOpen(false)
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              className="mt-2 w-full py-2 rounded-xl border border-dashed border-soft text-xs text-muted hover:text-brand hover:border-brand/40 inline-flex items-center justify-center gap-1 transition-colors"
              title="Add an item of your own to the queue — it lands on your pages with the rest">
        <Plus className="w-3.5 h-3.5" /> add your own item
      </button>
    )
  }
  return (
    <div className="mt-2 flex items-center gap-1.5 rounded-xl border border-soft p-1.5">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(false) }}
        placeholder="What is it?"
        className="flex-1 min-w-0 text-sm px-2 py-1 rounded-lg border border-soft bg-surface text-primary focus:outline-none focus:border-brand/50"
      />
      <select value={type} onChange={(e) => setType(e.target.value as MaterializedType)}
              className="text-xs px-1.5 py-1 rounded-lg border border-soft bg-surface text-muted">
        {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <button onClick={submit} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-brand text-white hover:bg-brand-hover disabled:opacity-40" disabled={!title.trim()}>add</button>
      <button onClick={() => setOpen(false)} className="text-xs px-2 py-1 rounded-lg border border-soft text-muted">cancel</button>
    </div>
  )
}

export function LifecoachLifeMap({ refreshSignal = 0, onCost }: LifecoachLifeMapProps) {
  const [data, setData] = useState<LifeMapResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const dataRef = useRef<LifeMapResponse | null>(null)
  dataRef.current = data
  // A ref, so the poll effect never re-arms when the parent re-renders.
  const onCostRef = useRef(onCost)
  onCostRef.current = onCost
  const reportCost = useCallback((res: LifeMapResponse) => {
    if (res.available && typeof res.cost_lifetime === 'number') {
      onCostRef.current?.(res.cost_lifetime)
    }
  }, [])

  // Review-diff state. The baseline is per saas user, per device.
  const userId = useSelector((st: RootState) => st.auth.user?.id) || 'anon'
  const [baseline, setBaseline] = useState<ReviewBaseline | null>(null)
  const [view, setView] = useState<'changes' | 'all'>('all')
  useEffect(() => {
    const b = loadBaseline(String(userId))
    setBaseline(b)
    if (b) setView('changes')
  }, [userId])

  // Materialization: engine map → real items on the site pages, after the
  // user's own edits (retitle / retype / replace-parent / leave-off / add).
  const [ledger, setLedger] = useState<Ledger>({})
  const [addingIds, setAddingIds] = useState<Set<number>>(new Set())
  const [addError, setAddError] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<Overrides>({})
  const [custom, setCustom] = useState<CustomDraft[]>([])
  // Version history: every action pushes a snapshot; the cursor walks them.
  const [history, setHistory] = useState<MapHistory | null>(null)
  const historyRef = useRef<MapHistory | null>(null)
  historyRef.current = history
  const [historyBusy, setHistoryBusy] = useState(false)
  const [showVersions, setShowVersions] = useState(false)
  useEffect(() => {
    const uid = String(userId)
    setLedger(loadLedger(uid))
    setOverrides(loadOverrides(uid))
    setCustom(loadCustom(uid))
    // Version history: resume, or seed version 1 from whatever is stored so
    // there is always a state to roll back to.
    const h = loadHistory(uid) ?? {
      states: [{
        label: 'starting point',
        at: new Date().toISOString(),
        overrides: loadOverrides(uid),
        custom: loadCustom(uid),
        committed: Object.keys(loadLedger(uid)),
      }],
      cursor: 0,
    }
    historyRef.current = h
    setHistory(h)
    saveHistory(uid, h)
  }, [userId])
  const overridesRef = useRef(overrides)
  overridesRef.current = overrides
  const customRef = useRef(custom)
  customRef.current = custom

  // All four mutators read the ref and write ref + state + storage together —
  // the ref is updated SYNCHRONOUSLY so several patches in one event handler
  // (the editor's save touches title, fields, parent and children) compose
  // instead of each starting from the pre-render snapshot.
  const patchOverride = useCallback((id: number, patch: Partial<Overrides[string]>) => {
    const key = String(id)
    const prev = overridesRef.current
    const merged: Record<string, unknown> = { ...prev[key], ...patch }
    for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k]
    const next: Overrides = { ...prev }
    if (Object.keys(merged).length === 0) delete next[key]
    else next[key] = merged
    overridesRef.current = next
    setOverrides(next)
    saveOverrides(String(userId), next)
  }, [userId])

  const patchCustom = useCallback((id: number, patch: Partial<CustomDraft>) => {
    const next = customRef.current.map((d) => (d.id === id ? { ...d, ...patch } : d))
    customRef.current = next
    setCustom(next)
    saveCustom(String(userId), next)
  }, [userId])

  const addCustom = useCallback((title: string, type: MaterializedType) => {
    const next = [...customRef.current, { id: -Date.now(), title, type, parent: null }]
    customRef.current = next
    setCustom(next)
    saveCustom(String(userId), next)
  }, [userId])

  const removeCustomDraft = useCallback((id: number) => {
    const next = customRef.current.filter((d) => d.id !== id)
    customRef.current = next
    setCustom(next)
    saveCustom(String(userId), next)
  }, [userId])

  // Effective (user-edited) views of an item.
  const effTypeF = useCallback((i: LifeMapItem): MaterializedType | null => {
    return overrides[String(i.id)]?.type ?? materializableType(i)
  }, [overrides])
  const titleOfF = useCallback((i: LifeMapItem): string => {
    return overrides[String(i.id)]?.title || i.display_title || i.title
  }, [overrides])
  const isExcludedF = useCallback((id: number): boolean => {
    return Boolean(overrides[String(id)]?.excluded)
  }, [overrides])
  const isPlacedF = useCallback((id: number): boolean => {
    const ov = overrides[String(id)]
    return Boolean(ov && 'parent' in ov)
  }, [overrides])

  const treeRef = useRef<TreeIndex | null>(null)
  // Mirror of the engine-parent map (assigned where it is computed below);
  // resolveParent reads it at event time, never during render.
  const engineParentMapRef = useRef<Map<number, number>>(new Map())

  const addStateOf = useCallback((item: LifeMapItem): AddState => {
    if (!effTypeF(item)) return 'unsupported'
    if (ledger[String(item.id)]) return 'added'
    if (addingIds.has(item.id)) return 'adding'
    return 'none'
  }, [ledger, addingIds, effTypeF])

  // Every item the panel knows, straight from the refs (engine rows + the
  // CURRENT custom drafts) — never from treeRef, which is one render behind
  // whatever a mutator or a version-restore just wrote.
  const allItemsMap = useCallback((): Map<number, LifeMapItem> => {
    const m = new Map<number, LifeMapItem>((dataRef.current?.map ?? []).map((i) => [i.id, i]))
    for (const d of customRef.current) m.set(d.id, customToItem(d))
    return m
  }, [])

  // The DISPLAYED parent, computed from the refs directly (user placement →
  // custom draft parent → engine edge). This is what materialize/sync walk:
  // unlike treeRef it is never one render behind a placement that was
  // written a microtask ago.
  const resolveParent = useCallback((id: number): LifeMapItem | null => {
    const byId = allItemsMap()
    const get = (pid: number | null | undefined) => (pid != null ? byId.get(pid) ?? null : null)
    if (id < 0) return get(customRef.current.find((c) => c.id === id)?.parent)
    const ov = overridesRef.current[String(id)]
    if (ov && 'parent' in ov) return get(ov.parent)
    return get(engineParentMapRef.current.get(id))
  }, [allItemsMap])

  const matDeps = useCallback((): MaterializeDeps => ({
    uid: String(userId),
    parentOf: (id) => resolveParent(id),
    childrenOf: (id) => {
      return [...allItemsMap().values()].filter((i) => resolveParent(i.id)?.id === id)
    },
    typeOf: (i) => {
      const ov = overridesRef.current[String(i.id)]
      if (ov?.excluded) return null
      return ov?.type ?? materializableType(i)
    },
    titleOf: (i) => overridesRef.current[String(i.id)]?.title || i.display_title || i.title,
    overrideOf: (i) => overridesRef.current[String(i.id)],
  }), [userId, resolveParent, allItemsMap])

  const addItem = useCallback(async (item: LifeMapItem): Promise<boolean> => {
    setAddError(null)
    setAddingIds((prev) => new Set(prev).add(item.id))
    try {
      const created = await materialize(item, matDeps())
      setLedger((prev) => ({ ...prev, ...created }))
      return true
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not create that item.')
      return false
    } finally {
      setAddingIds((prev) => { const n = new Set(prev); n.delete(item.id); return n })
    }
  }, [matDeps])

  /** Push the map's current view of a COMMITTED item to its page item. */
  const syncItem = useCallback(async (item: LifeMapItem) => {
    setAddError(null)
    setAddingIds((prev) => new Set(prev).add(item.id))
    try {
      const changed = await syncMaterialized(item, matDeps())
      setLedger((prev) => ({ ...prev, ...changed }))
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not update that page item.')
    } finally {
      setAddingIds((prev) => { const n = new Set(prev); n.delete(item.id); return n })
    }
  }, [matDeps])

  /** Fire-and-forget, but strictly one at a time — the ledger is a
   * read-modify-write file and parallel syncs would drop each other. */
  const syncSequential = useCallback((items: LifeMapItem[]) => {
    if (!items.length) return
    void (async () => {
      for (const it of items) {
        await syncItem(it)
      }
    })()
  }, [syncItem])

  // A page copy FOLLOWS the map (owner, 2026-09-12: "if I save and then
  // answer, it does not record"). They saved "Buy eggs" from the panel, then
  // told the chat "6"; the engine refined its row to 18:00 and the saved task
  // kept no hour, because acceptance copied the row once and nothing ever
  // looked again. Every map read now compares each committed item against
  // the fingerprint stamped at its last write and pushes the ones that
  // moved — once per fingerprint, so a failing push does not retry on every
  // poll tick.
  const followTried = useRef<Set<string>>(new Set())
  const followMap = useCallback((res: LifeMapResponse) => {
    if (!res.available || !userId) return
    const stale = staleCommitted(res.map, loadLedger(String(userId)))
      .filter((it) => {
        const key = `${it.id}:${JSON.stringify(it.temporal ?? null)}:${it.display_title ?? ''}:${it.title}`
        if (followTried.current.has(key)) return false
        followTried.current.add(key)
        return true
      })
    if (stale.length) syncSequential(stale)
  }, [userId, syncSequential])

  // ── Version history: undo / redo / roll to any state ────────────────────

  /** Effective title straight from the refs — for history labels. */
  const nameOf = useCallback((id: number): string => {
    const it = allItemsMap().get(id)
    if (!it) return `#${id}`
    return overridesRef.current[String(id)]?.title || it.display_title || it.title
  }, [allItemsMap])

  /** Push a snapshot of the CURRENT state as the newest version. Called
   * exactly once per user action (an editor save is one entry, not five);
   * anything ahead of the cursor — the redo tail — is discarded. */
  const commitHistory = useCallback((label: string) => {
    const uid = String(userId)
    const entry: HistoryEntry = {
      label,
      at: new Date().toISOString(),
      overrides: overridesRef.current,
      custom: customRef.current,
      committed: Object.keys(loadLedger(uid)),
    }
    const h = historyRef.current
    const states = h ? [...h.states.slice(0, h.cursor + 1), entry].slice(-HISTORY_MAX) : [entry]
    const nh: MapHistory = { states, cursor: states.length - 1 }
    historyRef.current = nh
    setHistory(nh)
    saveHistory(uid, nh)
  }, [userId])

  /**
   * Roll to any version — undo, redo, or a straight jump from the list.
   * Three phases: restore the editing state, reconcile page MEMBERSHIP
   * (delete what that version didn't have, recreate what it did), then
   * re-sync every still-committed item whose state differs or whose
   * ancestry was touched. All sequential; ids are engine ids, so recreated
   * page items simply get fresh saas ids in the ledger.
   */
  const jumpToVersion = useCallback(async (target: number) => {
    const h = historyRef.current
    if (!h || historyBusy || target < 0 || target >= h.states.length || target === h.cursor) return
    const uid = String(userId)
    const prev = h.states[h.cursor]
    const next = h.states[target]
    setHistoryBusy(true)
    setAddError(null)
    try {
      // 1. editing state — refs first, so every walk below sees it
      overridesRef.current = next.overrides
      setOverrides(next.overrides)
      saveOverrides(uid, next.overrides)
      customRef.current = next.custom
      setCustom(next.custom)
      saveCustom(uid, next.custom)

      // 2. membership
      const items = allItemsMap()
      let ledgerNow = loadLedger(uid)
      const want = new Set(next.committed)
      const removedKeys = Object.keys(ledgerNow).filter((k) => !want.has(k))
      const addedKeys = next.committed.filter((k) => !ledgerNow[k])
      for (const k of removedKeys) {
        await removeMaterializedByKey(uid, k)
      }
      for (const k of addedKeys) {
        const it = items.get(Number(k))
        if (it) await materialize(it, matDeps())
      }

      // 3. re-sync committed items whose stored edits differ between the two
      //    versions, or that sit under a node whose membership changed (a
      //    recreated ancestor has a new saas id to point at).
      ledgerNow = loadLedger(uid)
      const membershipChanged = new Set([...removedKeys, ...addedKeys].map(Number))
      const prevCustom = new Map(prev.custom.map((c) => [c.id, c]))
      const nextCustom = new Map(next.custom.map((c) => [c.id, c]))
      const editsDiffer = (idNum: number): boolean => {
        const k = String(idNum)
        if (JSON.stringify(prev.overrides[k] ?? null) !== JSON.stringify(next.overrides[k] ?? null)) return true
        return JSON.stringify(prevCustom.get(idNum) ?? null) !== JSON.stringify(nextCustom.get(idNum) ?? null)
      }
      const touchesChangedAncestry = (idNum: number): boolean => {
        let cur: LifeMapItem | null = items.get(idNum) ?? null
        const g = new Set<number>()
        while (cur && !g.has(cur.id)) {
          g.add(cur.id)
          cur = resolveParent(cur.id)
          if (cur && membershipChanged.has(cur.id)) return true
        }
        return false
      }
      for (const k of Object.keys(ledgerNow)) {
        if (addedKeys.includes(k)) continue          // freshly created — already right
        const idNum = Number(k)
        const it = items.get(idNum)
        if (!it) continue
        if (editsDiffer(idNum) || touchesChangedAncestry(idNum)) {
          await syncMaterialized(it, matDeps())
        }
      }
      setLedger(loadLedger(uid))

      // 4. the cursor moves; the states themselves stay
      const nh: MapHistory = { ...h, cursor: target }
      historyRef.current = nh
      setHistory(nh)
      saveHistory(uid, nh)
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not restore that version.')
    } finally {
      setHistoryBusy(false)
    }
  }, [userId, historyBusy, allItemsMap, matDeps, resolveParent])

  /** Delete the page item and return this row to being a proposal. */
  const removeFromPagesF = useCallback(async (item: LifeMapItem) => {
    const entry = loadLedger(String(userId))[String(item.id)]
    if (!entry) return
    const title = overridesRef.current[String(item.id)]?.title || item.display_title || item.title
    if (!window.confirm(`Remove “${title}” from your pages? Its ${entry.type} page item will be deleted. It stays here on the map as a proposal.`)) return
    setAddError(null)
    setAddingIds((prev) => new Set(prev).add(item.id))
    try {
      await removeMaterialized(item, String(userId))
      setLedger((prev) => { const n = { ...prev }; delete n[String(item.id)]; return n })
      commitHistory(`removed “${title}” from pages`)
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not remove that page item.')
    } finally {
      setAddingIds((prev) => { const n = new Set(prev); n.delete(item.id); return n })
    }
  }, [userId, commitHistory])

  const markReviewed = useCallback(() => {
    const d = dataRef.current
    if (!d?.map) return
    // Parent from the ENGINE's edges (not the user's placements): the diff
    // answers "what did consolidation do", and a removal ghosts back where
    // the ENGINE had put it.
    const ids = new Set(d.map.map((i) => i.id))
    const parentMap = new Map<number, number>()
    for (const e of d.map_edges || []) {
      if (HIER.has(e.kind) && ids.has(e.src) && ids.has(e.dst) && !parentMap.has(e.src)) {
        parentMap.set(e.src, e.dst)
      }
    }
    const b: ReviewBaseline = {
      reviewedAt: new Date().toISOString(),
      items: Object.fromEntries(d.map.map((i) => [String(i.id), {
        f: fingerprint(i),
        t: i.display_title || i.title,
        p: parentMap.get(i.id) ?? null,
        k: i.kind,
      }])),
    }
    try { localStorage.setItem(reviewKey(String(userId)), JSON.stringify(b)) } catch { /* full */ }
    setBaseline(b)
    setView('changes')
  }, [userId])

  const [pending, setPending] = useState<RegroupProposal | null>(null)
  const [regroupBusy, setRegroupBusy] = useState(false)
  const [regroupMsg, setRegroupMsg] = useState<string | null>(null)
  // The "being organized" strip + the Analyze button.
  const [pendingObs, setPendingObs] = useState<PendingResponse | null>(null)
  const [analyzeBusy, setAnalyzeBusy] = useState(false)
  const [analyzeMsg, setAnalyzeMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await hortonV3Service.getLifeMap()
      setData(res)
      reportCost(res)
      followMap(res)
      // The pending proposal and observations ride the same refresh; both
      // are best-effort reads and neither may fail the map.
      if (res.available) {
        const [pnd, pend] = await Promise.all([
          hortonV3Service.getRegroupPending(),
          hortonV3Service.getPending(),
        ])
        setPending(pnd)
        setPendingObs(pend)
      }
    } finally {
      setLoading(false)
    }
  }, [reportCost, followMap])

  const propose = useCallback(async () => {
    setRegroupBusy(true)
    setRegroupMsg(null)
    try {
      const out = await hortonV3Service.proposeRegroup()
      if (!out) { setRegroupMsg('The engine could not draft a regroup just now.'); return }
      if ((out as { status?: string }).status === 'no_change') {
        setRegroupMsg('Nothing to move — the map is already the tree the engine would draw.')
        return
      }
      setPending(out as RegroupProposal)
      setRegroupMsg(null)
    } finally {
      setRegroupBusy(false)
    }
  }, [])

  const decide = useCallback(async (id: number, d: 'accept' | 'reject') => {
    setRegroupBusy(true)
    try {
      const r = await hortonV3Service.decideRegroup(id, d)
      if (!r.ok) { setRegroupMsg(r.error ? `Could not ${d}: ${r.error}` : `Could not ${d}.`); return }
      setPending(null)
      setRegroupMsg(d === 'accept' ? 'Applied. The map has been regrouped.' : 'Rejected. Nothing moved.')
      await load()
    } finally {
      setRegroupBusy(false)
    }
  }, [load])

  const analyze = useCallback(async () => {
    setAnalyzeBusy(true)
    setAnalyzeMsg(null)
    try {
      const r = await hortonV3Service.analyzeMap()
      if (!r.ok) {
        setAnalyzeMsg(r.error ? `Couldn’t analyze: ${r.error}` : 'Couldn’t analyze just now.')
        return
      }
      if (r.status === 'already_running') {
        setAnalyzeMsg('Already analyzing — your map will update in a moment.')
      } else if ((r.mutations ?? 0) > 0) {
        setAnalyzeMsg(`Organized — ${r.mutations} change${r.mutations === 1 ? '' : 's'} to your map.`)
      } else {
        setAnalyzeMsg('Analyzed — nothing new to add right now.')
      }
      await load()   // refresh the map AND the pending strip
    } finally {
      setAnalyzeBusy(false)
    }
  }, [load])

  useEffect(() => { void load() }, [load, refreshSignal])

  // Async surfacing: consolidation lands on the scheduler's clock, not the
  // turn's. After each turn (refreshSignal bump) poll the map every few
  // seconds for up to ~3 minutes and stop early once the item count moves —
  // so items appear without a page reload. Cheap: /lifemap is one SELECT.
  const lastCount = useRef<number>(-1)
  useEffect(() => {
    if (refreshSignal === 0) return
    let ticks = 0
    let stopped = false
    let stopAfterTick = Infinity
    // Baseline from the FRESHEST data (a ref, not the value closed over when
    // the effect was created); if the initial load has not resolved yet the
    // first successful poll becomes the baseline instead of reading as a change.
    lastCount.current = dataRef.current ? dataRef.current.map.length : -1
    const id = window.setInterval(async () => {
      if (stopped) return
      ticks += 1
      try {
        const res = await hortonV3Service.getLifeMap()
        if (stopped) return
        // Capture is async (the worker writes observations a beat after the
        // reply), so refresh the "being organized" strip on the same ticks —
        // it is a cheap read and it is what makes "Compass heard you" appear
        // without a page reload.
        void hortonV3Service.getPending().then((p) => { if (!stopped) setPendingObs(p) })
        // A failed poll (proxy hiccup, expired session) comes back as an
        // "unavailable, empty" stub. That is not an empty map: keep what is
        // on screen, keep polling, and never let 0 read as "count changed".
        if (res.available) {
          setData(res)
          reportCost(res)
          followMap(res)
          if (lastCount.current < 0) {
            lastCount.current = res.map.length
          } else if (res.map.length !== lastCount.current && stopAfterTick === Infinity) {
            lastCount.current = res.map.length
            // one more tick after a change to catch the edges landing, then stop
            stopAfterTick = ticks + 1
          }
        }
      } catch { /* transient — keep polling */ }
      if (ticks >= stopAfterTick || ticks >= 24) { stopped = true; window.clearInterval(id) }   // ~3 min at 8s
    }, 8000)
    return () => { stopped = true; window.clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal])

  // Grouped by ROOT — the user's own directions and dreams — not by life
  // area. The tree IS the structure; the area is a tag on each item (shown as
  // a chip). Grouping the roots under area headings was a display habit that
  // made a "meal prep" filed under "major crossroads" read as if the map
  // meant it. Roots order: dreams, then directions, then the rest — and the
  // subtree with more under it first, so a rich branch reads before a stub.
  //
  // The tree is built over engine items PLUS the user's own drafts, and the
  // user's drag-and-drop placements replace the engine's edge for that item
  // (first edge wins in buildTree, so placement edges go first).
  const { tree, sections } = useMemo(() => {
    if (!data) return { tree: null as TreeIndex | null, sections: [] as Array<{ key: string; label: string; roots: LifeMapItem[] }> }
    const items = [...data.map, ...custom.map(customToItem)]
    const ids = new Set(items.map((i) => i.id))
    const edges: LifeMapEdge[] = []
    for (const [key, ov] of Object.entries(overrides)) {
      if (ov.parent != null && ids.has(Number(key)) && ids.has(ov.parent)) {
        edges.push({ src: Number(key), dst: ov.parent, kind: 'serves' })
      }
    }
    for (const d of custom) {
      if (d.parent != null && ids.has(d.parent)) edges.push({ src: d.id, dst: d.parent, kind: 'serves' })
    }
    for (const e of data.map_edges || []) {
      const ov = overrides[String(e.src)]
      // The user placed this item (possibly at top level: parent === null) —
      // the engine's hierarchy edge for it is suppressed either way.
      if (HIER.has(e.kind) && ov && 'parent' in ov) continue
      edges.push(e)
    }
    const t = buildTree(items, edges)
    const size = (id: number): number => 1 + (t.childrenOf.get(id) || []).reduce((n, c) => n + size(c), 0)
    // Anchoring reads the EFFECTIVE type: something the user converted to a
    // dream earns its own card at the top, exactly like an engine dream.
    const et = (i: LifeMapItem) => overrides[String(i.id)]?.type ?? materializableType(i)
    const rank = (r: LifeMapItem) => (et(r) === 'dream' ? (r.kind === 'direction' ? 1 : 0) : et(r) === 'goal' ? 2 : 3)
    const anchored = t.roots.filter((r) => et(r) === 'dream' || (t.childrenOf.get(r.id) || []).length > 0)
    // Errands are their OWN section, not "not yet placed": nothing is
    // waiting to place them. A task the person gave no purpose for has no
    // parent by rule (engine guard G1c) and is left out of the narrative;
    // showing it as an unplaced root reads like a failure when it is the
    // correct shape for "buy eggs".
    const errands = t.roots.filter((r) => r.ad_hoc)
    const loose = t.roots.filter((r) => !anchored.includes(r) && !r.ad_hoc)
    anchored.sort((a, b) => rank(a) - rank(b) || size(b.id) - size(a.id) || a.id - b.id)
    loose.sort((a, b) => b.confidence - a.confidence || a.id - b.id)
    const secs: Array<{ key: string; label: string; roots: LifeMapItem[] }> = anchored.map((r) => ({
      key: `root-${r.id}`, label: r.display_title || r.title, roots: [r],
    }))
    if (loose.length) secs.push({ key: 'loose', label: 'Not yet placed', roots: loose })
    if (errands.length) {
      errands.sort((a, b) => b.id - a.id)          // newest errand first
      secs.push({ key: 'adhoc', label: 'Ad-hoc', roots: errands })
    }
    return { tree: t, sections: secs }
  }, [data, overrides, custom])

  treeRef.current = tree

  // ── The queue: everything not yet on the pages, minus what you unchecked ──
  const [deselected, setDeselected] = useState<Set<number>>(new Set())
  const pendingItems = useMemo(() => {
    if (!tree) return [] as LifeMapItem[]
    const out: LifeMapItem[] = []
    for (const i of tree.byId.values()) {
      if (isExcludedF(i.id)) continue
      if (ledger[String(i.id)]) continue
      if (!effTypeF(i)) continue
      out.push(i)
    }
    return out
  }, [tree, ledger, isExcludedF, effTypeF])
  const queuedItems = useMemo(
    () => pendingItems.filter((i) => !deselected.has(i.id)),
    [pendingItems, deselected],
  )
  const queuedRef = useRef(queuedItems)
  queuedRef.current = queuedItems
  const excludedCount = useMemo(
    () => Object.values(overrides).filter((o) => o.excluded).length,
    [overrides],
  )

  const [applying, setApplying] = useState<{ done: number; total: number } | null>(null)
  const applyItems = useCallback(async (items: LifeMapItem[]) => {
    const t = treeRef.current
    const depthOf = (id: number): number => {
      let d = 0
      let cur = t?.parentOf.get(id)?.parent
      const g = new Set([id])
      while (cur != null && !g.has(cur)) { d += 1; g.add(cur); cur = t?.parentOf.get(cur)?.parent }
      return d
    }
    // Parents first — materialize creates ancestors anyway, but this order
    // makes the ledger fill top-down with no redundant chain walks.
    const todo = [...items].sort((a, b) => depthOf(a.id) - depthOf(b.id))
    setApplying({ done: 0, total: todo.length })
    let ok = 0
    try {
      for (const i of todo) {
        if (!loadLedger(String(userId))[String(i.id)]) {
          // sequential on purpose: parents before children, no request storm
          if (await addItem(i)) ok += 1
        }
        setApplying((p) => (p ? { ...p, done: p.done + 1 } : p))
      }
    } finally {
      setApplying(null)
      if (ok > 0) commitHistory(`added ${ok} item${ok === 1 ? '' : 's'} to pages`)
    }
  }, [addItem, userId, commitHistory])
  const applyQueued = useCallback(() => applyItems(queuedRef.current), [applyItems])

  // ── The editor: all fields + relationships, committed as one save ────────
  const [editingId, setEditingId] = useState<number | null>(null)
  const openEditor = useCallback((id: number) => setEditingId(id), [])
  const closeEditor = useCallback(() => setEditingId(null), [])

  // Engine parents from the RAW edges (not the user's placements): the
  // editor's "engine's placement" option must name what the ENGINE chose.
  const engineParentMap = useMemo(() => {
    const m = new Map<number, number>()
    const ids = new Set((data?.map ?? []).map((i) => i.id))
    for (const e of data?.map_edges ?? []) {
      if (HIER.has(e.kind) && ids.has(e.src) && ids.has(e.dst) && !m.has(e.src)) m.set(e.src, e.dst)
    }
    return m
  }, [data])
  engineParentMapRef.current = engineParentMap

  const initialParentSel = useCallback((id: number): ParentSel => {
    if (id < 0) {
      const p = customRef.current.find((c) => c.id === id)?.parent
      return p == null ? 'top' : p
    }
    const ov = overridesRef.current[String(id)]
    if (ov && 'parent' in ov) return ov.parent == null ? 'top' : ov.parent
    return 'engine'
  }, [])

  const enginePlacementName = useCallback((id: number): string | null => {
    const pid = engineParentMap.get(id)
    if (pid == null) return null
    const p = treeRef.current?.byId.get(pid)
    return p ? (overridesRef.current[String(pid)]?.title || p.display_title || p.title) : null
  }, [engineParentMap])

  /** Is `candidate` inside the DISPLAYED subtree rooted at `rootId`? */
  const isInSubtree = useCallback((candidate: number, rootId: number): boolean => {
    const t = treeRef.current
    let cur: number | undefined = candidate
    const g = new Set<number>()
    while (cur != null && !g.has(cur)) {
      if (cur === rootId) return true
      g.add(cur)
      cur = t?.parentOf.get(cur)?.parent
    }
    return false
  }, [])

  // The ladder, applied: valid parents for an item IF it were `childType`.
  // Dreams take no parent at all; goals sit under dreams; leaves under
  // dreams or goals. Cycles and excluded items are never offered. Already-
  // created items are fine — the chain walk reuses their page item.
  const eligibleParentsFor = useCallback((id: number, childType: MaterializedType): LifeMapItem[] => {
    const t = treeRef.current
    if (!t) return []
    const allowed = PARENT_TYPES[childType]
    if (!allowed.length) return []
    const out = [...t.byId.values()].filter((p) => {
      if (p.id === id || isExcludedF(p.id) || isInSubtree(p.id, id)) return false
      const pt = effTypeF(p)
      return pt != null && allowed.includes(pt)
    })
    out.sort((a, b) => titleOfF(a).localeCompare(titleOfF(b)))
    return out
  }, [isExcludedF, isInSubtree, effTypeF, titleOfF])

  // Children an item of `parentType` could hold: not excluded, the ladder
  // allows the nesting, no loops, not already inside. Committed items are
  // eligible too — pulling one under re-points its page item.
  const eligibleChildrenFor = useCallback((id: number, parentType: MaterializedType): LifeMapItem[] => {
    const t = treeRef.current
    if (!t || !canHoldChildren(parentType)) return []
    const out = [...t.byId.values()].filter((c) => {
      if (c.id === id || isExcludedF(c.id)) return false
      const ct = effTypeF(c)
      if (!ct || !PARENT_TYPES[ct].includes(parentType)) return false
      return !isInSubtree(id, c.id) && t.parentOf.get(c.id)?.parent !== id
    })
    out.sort((a, b) => titleOfF(a).localeCompare(titleOfF(b)))
    return out
  }, [isExcludedF, effTypeF, isInSubtree, titleOfF])

  const place = useCallback((id: number, parent: number | null) => {
    if (id < 0) patchCustom(id, { parent })
    else patchOverride(id, { parent })
  }, [patchCustom, patchOverride])

  // Retype with the ladder applied: a dream moves to the top level (dreams
  // have no parent, full stop); any other type keeps its spot unless the
  // USER had placed it somewhere the new type cannot sit — that placement
  // reverts to the engine's. Engine edges are never re-shaped here.
  const setTypeChecked = useCallback((item: LifeMapItem, t: MaterializedType) => {
    if (item.id < 0) patchCustom(item.id, { type: t })
    else patchOverride(item.id, { type: t })
    if (t !== 'dream') {
      const userParent = item.id < 0
        ? customRef.current.find((c) => c.id === item.id)?.parent
        : (() => { const ov = overridesRef.current[String(item.id)]; return ov && 'parent' in ov ? ov.parent : undefined })()
      if (typeof userParent === 'number') {
        const p = treeRef.current?.byId.get(userParent)
        const pt = p ? (overridesRef.current[String(p.id)]?.type ?? materializableType(p)) : null
        if (!pt || !PARENT_TYPES[t].includes(pt)) {
          if (item.id < 0) patchCustom(item.id, { parent: null })
          else patchOverride(item.id, { parent: undefined })
        }
      }
    } else {
      place(item.id, null)
    }
    commitHistory(`retyped “${nameOf(item.id)}” to ${t}`)
    // A committed item follows immediately: retype recreates its page item.
    if (loadLedger(String(userId))[String(item.id)]) syncSequential([item])
  }, [patchCustom, patchOverride, place, userId, syncSequential, commitHistory, nameOf])

  const saveEditor = useCallback((item: LifeMapItem, draft: EditorDraft, initParent: ParentSel) => {
    const id = item.id
    const isCustom = id < 0
    const t = draft.title.trim()

    // Fields are stored ONLY where they differ from the engine defaults, so
    // reverting a field in the editor also removes its override.
    const def = defaultFieldsOf(item)
    const sameDays = (a: Weekday[], b?: Weekday[]) => {
      const bb = b ?? []
      return a.length === bb.length && a.every((x) => bb.includes(x))
    }
    const durNum = draft.duration.trim() === '' ? undefined : Math.max(0, parseInt(draft.duration, 10) || 0)
    const patch: Partial<ItemOverride> = {
      description: draft.description !== (def.description ?? '') ? draft.description : undefined,
      priority: draft.priority !== def.priority ? draft.priority : undefined,
      area: draft.area.trim() !== (def.area ?? '') ? draft.area.trim() : undefined,
      cadence: draft.cadence !== (def.cadence ?? '') ? draft.cadence : undefined,
      days: !sameDays(draft.days, def.days) ? draft.days : undefined,
      window: draft.window !== (def.window ?? '') ? (draft.window as TimeWindow | '') : undefined,
      at: draft.at !== (def.at ?? '') ? draft.at : undefined,
      duration: (durNum ?? 0) !== (def.duration ?? 0) ? (durNum ?? 0) : undefined,
      due: draft.due !== (def.due ?? '') ? draft.due : undefined,
    }
    if (isCustom) {
      patchCustom(id, t ? { title: t, type: draft.type } : { type: draft.type })
      patchOverride(id, patch)
    } else {
      patchOverride(id, {
        ...patch,
        title: t && t !== (item.display_title || item.title) ? t : undefined,
        type: draft.type !== materializableType(item) ? draft.type : undefined,
      })
    }

    // A dream sits at the top level regardless of what the select held.
    const parentSel: ParentSel = draft.type === 'dream' ? 'top' : draft.parent
    if (parentSel !== initParent) {
      if (isCustom) {
        patchCustom(id, { parent: typeof parentSel === 'number' ? parentSel : null })
      } else if (parentSel === 'engine') {
        patchOverride(id, { parent: undefined })
      } else {
        patchOverride(id, { parent: parentSel === 'top' ? null : parentSel })
      }
    }

    for (const cid of draft.children) {
      if (cid < 0) patchCustom(cid, { parent: id })
      else patchOverride(cid, { parent: id })
    }

    // Committed items follow the save immediately: the edited item itself,
    // and any committed children just pulled under it.
    const led = loadLedger(String(userId))
    const toSync: LifeMapItem[] = []
    if (led[String(id)]) toSync.push(item)
    for (const cid of draft.children) {
      if (led[String(cid)]) {
        const c = treeRef.current?.byId.get(cid)
        if (c) toSync.push(c)
      }
    }
    commitHistory(`edited “${t || nameOf(id)}”`)
    syncSequential(toSync)

    setEditingId(null)
  }, [patchCustom, patchOverride, userId, syncSequential, commitHistory, nameOf])

  // ── Drag & drop: place under a valid parent, at top level, or off ────────
  const [dragId, setDragId] = useState<number | null>(null)
  const [dragOverId, setDragOverId] = useState<number | null>(null)
  const [zoneOver, setZoneOver] = useState<'top' | 'off' | null>(null)
  const endDrag = useCallback(() => { setDragId(null); setDragOverId(null); setZoneOver(null) }, [])
  const draggedType = useMemo((): MaterializedType | null => {
    if (dragId == null) return null
    const it = treeRef.current?.byId.get(dragId)
    return it ? effTypeF(it) : null
  }, [dragId, effTypeF])
  const canDropOn = useCallback((targetId: number): boolean => {
    const t = treeRef.current
    if (dragId == null || targetId === dragId || !t) return false
    if (isExcludedF(targetId)) return false
    // The ladder: the target's type must be able to hold the dragged type.
    if (!draggedType) return false
    const allowed = PARENT_TYPES[draggedType]
    if (!allowed.length) return false           // dreams only go to the top
    const target = t.byId.get(targetId)
    const tt = target ? effTypeF(target) : null
    if (!tt || !allowed.includes(tt)) return false
    // The target must not sit inside the dragged subtree — that would loop.
    let cur: number | undefined = targetId
    const g = new Set<number>()
    while (cur != null && !g.has(cur)) {
      if (cur === dragId) return false
      g.add(cur)
      cur = t.parentOf.get(cur)?.parent
    }
    return true
  }, [dragId, draggedType, isExcludedF, effTypeF])
  const dropOn = useCallback((targetId: number) => {
    if (dragId != null && canDropOn(targetId)) {
      place(dragId, targetId)
      commitHistory(`moved “${nameOf(dragId)}” under “${nameOf(targetId)}”`)
      // A committed item's page item follows its new spot immediately.
      const it = treeRef.current?.byId.get(dragId)
      if (it && loadLedger(String(userId))[String(dragId)]) syncSequential([it])
    }
    endDrag()
  }, [dragId, canDropOn, place, endDrag, userId, syncSequential, commitHistory, nameOf])

  const itemCount = data?.map.length ?? 0
  const obsTotal = data?.observations_total ?? 0

  // What changed since the last review — badges keyed by id so the change
  // can be shown IN PLACE in the tree, plus the removals as ghosts.
  const diff = useMemo(() => {
    if (!data || !baseline) return null
    const badges = new Map<number, DiffBadge>()
    const added: LifeMapItem[] = []
    for (const i of data.map) {
      const prev = baseline.items[String(i.id)]
      if (!prev) { badges.set(i.id, { kind: 'new' }); added.push(i) }
      else if (prev.f !== fingerprint(i)) {
        badges.set(i.id, { kind: 'changed', fields: changedFields(prev.f, i) })
      }
    }
    const liveIds = new Set(data.map.map((i) => String(i.id)))
    const removed = Object.entries(baseline.items)
      .filter(([id]) => !liveIds.has(id))
      .map(([id, v]) => ({ id, title: v.t, parent: v.p, parentKnown: v.p !== undefined }))
    added.sort((a, b) => b.id - a.id)
    return {
      badges, added, removed,
      changedCount: badges.size - added.length,
      total: badges.size + removed.length,
    }
  }, [data, baseline])

  // A wiped map has nothing left to review. After clear-all-items /
  // lifecoach-reset the map legitimately comes back EMPTY while this
  // device's baseline still remembers every old item — which rendered as a
  // wall of "− N removed" pending changes the user could do nothing with
  // (owner report 2026-08-24: "26 changes pending" after a full wipe).
  // When a SUCCESSFUL load (available — never the unavailable stub, which
  // must not read as an empty map) returns zero items while the baseline
  // is non-empty, adopt the empty map as the new baseline instead of
  // asking the user to review a wipe they ran themselves.
  useEffect(() => {
    if (!data?.available) return
    if ((data.map?.length ?? 0) > 0) return
    if (!baseline || Object.keys(baseline.items).length === 0) return
    const b: ReviewBaseline = { reviewedAt: new Date().toISOString(), items: {} }
    try { localStorage.setItem(reviewKey(String(userId)), JSON.stringify(b)) } catch { /* full */ }
    setBaseline(b)
  }, [data, baseline, userId])

  // Removals ghosted under their old (engine) parent when it is still on the
  // map. Removals whose old spot is itself gone are counted in the legend
  // but not listed — the standalone "Removed" dump was retired (owner call,
  // 2026-08-23: "we are no longer using them").
  const ghosts = useMemo(() => {
    const under = new Map<number, Ghost[]>()
    if (diff && tree) {
      for (const r of diff.removed) {
        if (r.parentKnown && r.parent != null && tree.byId.has(r.parent)) {
          const list = under.get(r.parent) || []
          list.push({ id: r.id, title: r.title })
          under.set(r.parent, list)
        }
      }
    }
    return { under }
  }, [diff, tree])

  // The changes view shows changed nodes WHERE THEY SIT: each changed node,
  // its ancestors (dimmed, for context), and the parents removals ghost under.
  const changesVisible = useMemo(() => {
    if (!diff || !tree) return null
    const vis = new Set<number>()
    const up = (id: number) => {
      let cur: number | undefined = id
      const g = new Set<number>()
      while (cur != null && !g.has(cur)) { g.add(cur); vis.add(cur); cur = tree.parentOf.get(cur)?.parent }
    }
    for (const id of diff.badges.keys()) up(id)
    for (const pid of ghosts.under.keys()) up(pid)
    return vis
  }, [diff, tree, ghosts])

  const diffOf = useMemo(() => {
    if (!diff) return undefined
    return (id: number): DiffBadge | null => diff.badges.get(id) ?? null
  }, [diff])

  const baseCtx: RowCtx | null = tree ? {
    tree,
    addStateOf,
    onAdd: (it) => void (async () => {
      if (await addItem(it)) commitHistory(`added “${nameOf(it.id)}” to pages`)
    })(),
    titleOf: titleOfF,
    typeOf: effTypeF,
    isExcluded: isExcludedF,
    isPlaced: isPlacedF,
    isSelected: (id) => !deselected.has(id),
    toggleSelected: (id) => setDeselected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id); else n.add(id)
      return n
    }),
    setType: setTypeChecked,
    exclude: (id) => {
      patchOverride(id, { excluded: true })
      commitHistory(`left “${nameOf(id)}” off the pages`)
    },
    restore: (id) => {
      patchOverride(id, { excluded: undefined })
      commitHistory(`restored “${nameOf(id)}”`)
    },
    clearPlacement: (id) => {
      if (id < 0) patchCustom(id, { parent: null })
      else patchOverride(id, { parent: undefined })
      commitHistory(`reverted “${nameOf(id)}” to the engine’s spot`)
      const it = treeRef.current?.byId.get(id)
      if (it && loadLedger(String(userId))[String(id)]) syncSequential([it])
    },
    removeCustom: (id) => {
      const nm = nameOf(id)
      removeCustomDraft(id)
      commitHistory(`deleted draft “${nm}”`)
    },
    removeFromPages: (it) => void removeFromPagesF(it),
    openItemPage: (it) => {
      const entry = loadLedger(String(userId))[String(it.id)]
      if (entry) {
        // A new tab: the chat (and this panel's state) stays where it was.
        window.open(`/${TYPE_ROUTE[entry.type]}/${entry.saasId}`, '_blank', 'noopener')
      } else if (addStateOf(it) === 'none' && !isExcludedF(it.id)) {
        openEditor(it.id)
      }
    },
    editingId, openEditor, closeEditor, saveEditor,
    overrideOf: (id) => overrides[String(id)],
    initialParentSel, enginePlacementName, eligibleParentsFor, eligibleChildrenFor,
    drag: {
      dragId,
      start: setDragId,
      end: endDrag,
      overId: dragOverId,
      setOver: setDragOverId,
      canDropOn,
      dropOn,
    },
    diffOf,
  } : null

  const changesCtx: RowCtx | null = baseCtx ? {
    ...baseCtx,
    visibleIds: changesVisible,
    ghostsUnder: (id) => ghosts.under.get(id) ?? [],
    dimContext: true,
  } : null

  const visSections = useMemo(() => {
    if (!changesVisible) return sections
    return sections.filter((s) => s.roots.some((r) => changesVisible.has(r.id)))
  }, [sections, changesVisible])

  const renderSections = (ctx: RowCtx, secs: typeof sections) => secs.map(({ key, label, roots }) => {
    if (key === 'loose' || key === 'adhoc') {
      return (
        <div key={key} className="mb-3">
          <div className="flex items-center gap-1.5 text-app-nano font-semibold uppercase tracking-wider text-muted mb-1 px-1"
               title={key === 'adhoc'
                 ? 'One-off things to do. They have no parent by design, and they stay out of the narrative.'
                 : 'Items the map has not attached to a direction or dream yet'}>
            {key === 'adhoc' ? <CircleDashed className="w-3 h-3" /> : <Inbox className="w-3 h-3" />}
            {label}
          </div>
          {roots.map((r) => (
            <Node key={r.id} item={r} depth={0} ctx={ctx} />
          ))}
        </div>
      )
    }
    // One card per anchored root, its accent edge tinted by the root's type.
    const rootType = ctx.typeOf(roots[0]) ?? 'dream'
    return (
      <div key={key}
           className={`mb-3 rounded-xl border border-soft/80 border-l-2 ${TYPE_META[rootType]?.edge ?? ''} bg-surface-alt/20 px-1.5 py-1.5`}>
        {roots.map((r) => (
          <Node key={r.id} item={r} depth={0} ctx={ctx} />
        ))}
      </div>
    )
  })

  // The footer: big labeled drop zones while dragging, progress while
  // applying, and the one-button queue the rest of the time.
  const draggedCommitted = dragId != null && Boolean(ledger[String(dragId)])
  const footer = (dragId != null || applying || pendingItems.length > 0) ? (
    <div className="sticky bottom-0 z-10 -mx-3 px-2 pb-1.5 pt-1">
      <div className="rounded-xl border border-soft bg-surface/95 backdrop-blur shadow-lg px-2.5 py-2">
        {dragId != null ? (
          <div className="flex gap-2">
            <div
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setZoneOver('top') }}
              onDragLeave={() => setZoneOver((z) => (z === 'top' ? null : z))}
              onDrop={(e) => {
                e.preventDefault()
                place(dragId, null)
                commitHistory(`moved “${nameOf(dragId)}” to top level`)
                const it = treeRef.current?.byId.get(dragId)
                if (it && loadLedger(String(userId))[String(dragId)]) syncSequential([it])
                endDrag()
              }}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-3 rounded-lg border-2 border-dashed transition-colors
                ${zoneOver === 'top' ? 'border-brand bg-brand-subtle/60 text-brand' : 'border-brand/40 text-brand/80'}`}
            >
              <ArrowUpToLine className="w-3.5 h-3.5" />
              {draggedType === 'dream' ? 'Dreams live here — top level' : 'Move to top level'}
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setZoneOver('off') }}
              onDragLeave={() => setZoneOver((z) => (z === 'off' ? null : z))}
              onDrop={(e) => {
                e.preventDefault()
                const it = treeRef.current?.byId.get(dragId)
                const nm = nameOf(dragId)
                endDrag()
                if (it && loadLedger(String(userId))[String(dragId)]) {
                  // Committed: this zone means "take it off the pages" — the
                  // page item is deleted (confirmed) and the row returns to
                  // being a proposal.
                  void removeFromPagesF(it)
                } else if (dragId < 0) {
                  removeCustomDraft(dragId)
                  commitHistory(`deleted draft “${nm}”`)
                } else {
                  patchOverride(dragId, { excluded: true })
                  commitHistory(`left “${nm}” off the pages`)
                }
              }}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-3 rounded-lg border-2 border-dashed transition-colors
                ${zoneOver === 'off' ? 'border-bad bg-red-500/10 text-bad' : 'border-bad/40 text-bad/80'}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {draggedCommitted ? 'Remove from your pages' : 'Leave off the pages'}
            </div>
          </div>
        ) : applying ? (
          <div className="flex items-center gap-2 text-xs text-muted">
            <span>Adding to your pages… {applying.done}/{applying.total}</span>
            <span className="flex-1 h-1.5 rounded-full bg-surface-alt overflow-hidden">
              <span className="block h-full rounded-full bg-brand transition-all"
                    style={{ width: `${applying.total ? Math.round((applying.done / applying.total) * 100) : 0}%` }} />
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-muted">
              <strong className="text-secondary">{queuedItems.length}</strong> of {pendingItems.length} queued
              {excludedCount > 0 ? ` · ${excludedCount} left off` : ''}
            </span>
            <button onClick={() => setDeselected(new Set())}
                    className="px-1.5 py-0.5 rounded-md text-muted hover:text-brand hover:bg-surface-alt">all</button>
            <button onClick={() => setDeselected(new Set(pendingItems.map((i) => i.id)))}
                    className="px-1.5 py-0.5 rounded-md text-muted hover:text-brand hover:bg-surface-alt">none</button>
            <button onClick={() => void applyQueued()}
                    disabled={queuedItems.length === 0}
                    className="ml-auto px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-semibold hover:bg-brand-hover shadow-sm disabled:opacity-40 transition-colors"
                    title="Create every checked item on your site pages, parents included — one click, not one per item">
              Add {queuedItems.length} to pages
            </button>
          </div>
        )}
      </div>
    </div>
  ) : null

  return (
    <div
      className="h-full flex flex-col min-h-0"
      onKeyDown={(e) => {
        // Cmd/Ctrl+Z (+Shift = redo) when focus is inside the panel and not
        // in a text field — those own their native undo.
        if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return
        const t = e.target as HTMLElement
        if (t.closest('input,textarea,select,[contenteditable]')) return
        e.preventDefault()
        const h = historyRef.current
        if (h) void jumpToVersion(e.shiftKey ? h.cursor + 1 : h.cursor - 1)
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-soft flex-shrink-0">
        <span className="w-6 h-6 rounded-lg bg-brand-subtle/70 flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-brand" />
        </span>
        <span className="text-sm font-semibold text-primary">Life Map</span>
        {baseline ? (
          <span className="inline-flex rounded-full bg-surface-alt p-0.5 gap-0.5">
            <button
              onClick={() => setView('changes')}
              aria-pressed={view === 'changes'}
              className={`text-app-micro px-2 py-0.5 rounded-full transition-colors ${view === 'changes'
                ? 'bg-brand text-on-brand shadow-sm'
                : 'text-muted hover:text-primary'}`}
              title="Only what consolidation added, changed or removed since you last reviewed — shown where it sits in the tree"
            >
              Changes{diff ? ` ${diff.total}` : ''}
            </button>
            <button
              onClick={() => setView('all')}
              aria-pressed={view === 'all'}
              className={`text-app-micro px-2 py-0.5 rounded-full transition-colors ${view === 'all'
                ? 'bg-brand text-on-brand shadow-sm'
                : 'text-muted hover:text-primary'}`}
            >
              All {itemCount}
            </button>
          </span>
        ) : (
          <span className="text-xs text-muted">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
        )}
        {itemCount > 0 && (
          <button
            onClick={markReviewed}
            className="text-app-micro px-2 py-0.5 rounded-full border border-soft text-muted hover:text-brand hover:border-brand/40 inline-flex items-center gap-1 transition-colors"
            title="Snapshot the map as reviewed — the Changes tab will show only what differs from this moment"
          >
            <CheckCheck className="w-3 h-3" /> Mark reviewed
          </button>
        )}
        <span className="ml-auto flex items-center gap-0.5">
          <button
            onClick={() => { const h = historyRef.current; if (h) void jumpToVersion(h.cursor - 1) }}
            disabled={!history || history.cursor <= 0 || historyBusy}
            className="p-1.5 rounded-lg hover:bg-surface-alt text-muted disabled:opacity-30 transition-colors"
            aria-label="Undo"
            title={history && history.cursor > 0
              ? `Undo — ${history.states[history.cursor].label}`
              : 'Nothing to undo'}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => { const h = historyRef.current; if (h) void jumpToVersion(h.cursor + 1) }}
            disabled={!history || history.cursor >= history.states.length - 1 || historyBusy}
            className="p-1.5 rounded-lg hover:bg-surface-alt text-muted disabled:opacity-30 transition-colors"
            aria-label="Redo"
            title={history && history.cursor < history.states.length - 1
              ? `Redo — ${history.states[history.cursor + 1].label}`
              : 'Nothing to redo'}
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowVersions((v) => !v)}
            aria-pressed={showVersions}
            className={`p-1.5 rounded-lg transition-colors ${showVersions ? 'bg-brand-subtle text-brand' : 'hover:bg-surface-alt text-muted'}`}
            title="Version history — roll back or forward to any point"
          >
            <History className={`w-4 h-4 ${historyBusy ? 'animate-pulse' : ''}`} />
          </button>
          <button
            onClick={() => void load()}
            className="p-1.5 rounded-lg hover:bg-surface-alt text-muted transition-colors"
            aria-label="Refresh life map"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 min-h-0">
        {showVersions && history && (
          <div className="mb-3 rounded-xl border border-soft bg-surface-alt/30 p-2">
            <div className="flex items-center gap-2 mb-1.5 px-1">
              <History className="w-3 h-3 text-muted" />
              <span className="text-app-nano font-semibold uppercase tracking-wider text-muted">Versions</span>
              {historyBusy
                ? <span className="text-app-nano text-brand animate-pulse">restoring — your pages follow…</span>
                : <span className="text-app-nano text-muted">newest first · restoring also updates your pages</span>}
            </div>
            <div className="max-h-44 overflow-y-auto space-y-0.5">
              {history.states.map((s, i) => ({ s, i })).reverse().map(({ s, i }) => (
                <button
                  key={`${i}-${s.at}`}
                  onClick={() => void jumpToVersion(i)}
                  disabled={historyBusy || i === history.cursor}
                  className={`w-full flex items-center gap-2 text-left text-xs px-2 py-1 rounded-lg transition-colors ${
                    i === history.cursor
                      ? 'bg-brand-subtle/60 text-primary'
                      : 'hover:bg-surface-alt text-secondary disabled:opacity-50'
                  }`}
                >
                  <span className="flex-1 truncate">{s.label}</span>
                  <span className="text-app-nano text-muted whitespace-nowrap">
                    {new Date(s.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                  </span>
                  <span className={`text-app-nano font-semibold uppercase tracking-wide whitespace-nowrap ${
                    i === history.cursor ? 'text-brand' : 'text-muted'
                  }`}>
                    {i === history.cursor ? 'current' : i < history.cursor ? 'roll back' : 'roll forward'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Shown in BOTH states: when the map is empty (just said something,
            no items yet) is exactly when "being organized" matters most. */}
        <PendingStrip pending={pendingObs} busy={analyzeBusy} message={analyzeMsg}
                      onAnalyze={() => void analyze()} />
        {addError && (
          <div className="mb-2 text-xs text-bad rounded-lg border border-bad/30 bg-red-500/5 px-2 py-1.5">{addError}</div>
        )}
        {itemCount === 0 && custom.length === 0 ? (
          <div className="flex flex-col items-center text-center py-8 px-4">
            <span className="w-10 h-10 rounded-2xl bg-brand-subtle/60 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-brand" />
            </span>
            <div className="text-sm text-secondary leading-relaxed max-w-xs">
              Nothing on the map yet — items are promoted by consolidation, never by a single turn.
              {obsTotal > 0 ? (
                <>
                  {' '}
                  <strong className="text-primary">{obsTotal}</strong> observation
                  {obsTotal === 1 ? '' : 's'} recorded and waiting. Consolidation runs
                  automatically after a quiet period.
                </>
              ) : (
                <> Say something about your life and it will be captured within a turn.</>
              )}
            </div>
          </div>
        ) : baseline && view === 'changes' ? (
          <>
            {diff && diff.total === 0 ? (
              <div className="text-sm text-muted leading-relaxed">
                Nothing new since your last review
                {' '}({new Date(baseline.reviewedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}).
                New items land after consolidation runs — keep chatting, or press Analyze above.
              </div>
            ) : diff && changesCtx && (
              <>
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-app-micro text-muted mb-3">
                  <span>
                    Since your last review ({new Date(baseline.reviewedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}):
                  </span>
                  <span className="px-1.5 py-px rounded-full bg-emerald-500/10 text-emerald-500 font-semibold">+{diff.added.length} new</span>
                  <span className="px-1.5 py-px rounded-full bg-amber-500/10 text-warn font-semibold">↻ {diff.changedCount} changed</span>
                  <span className="px-1.5 py-px rounded-full bg-surface-alt text-muted font-semibold">− {diff.removed.length} removed</span>
                  {diff.added.length > 0 && (
                    <button
                      onClick={() => void applyItems(diff.added.filter((i) => !isExcludedF(i.id) && Boolean(effTypeF(i))))}
                      className="text-app-nano px-2 py-0.5 rounded-full border border-brand/40 text-brand hover:bg-brand-subtle transition-colors"
                      title="Create every new mappable item on your site pages"
                    >
                      + add all new to pages
                    </button>
                  )}
                </div>
                {renderSections(changesCtx, visSections)}
              </>
            )}
            <RegroupPanel pending={pending} busy={regroupBusy} message={regroupMsg}
                          onPropose={() => void propose()} onDecide={(id, d) => void decide(id, d)}
                          tree={tree} />
          </>
        ) : (
          <>
            {!baseline && itemCount > 8 && (
              <div className="mb-3 px-3 py-2 rounded-xl border border-brand/30 bg-brand-subtle/40 text-xs text-secondary">
                Reviewing all {itemCount} items each time? Press
                {' '}<strong>Mark reviewed</strong> above — next time this panel
                opens on just what changed.
              </div>
            )}
            {baseCtx && renderSections(baseCtx, sections)}
            <AddYourOwn onAdd={(t, ty) => { addCustom(t, ty); commitHistory(`added draft “${t}”`) }} />
            {data?.narrative?.body && (
              <div className="mt-4 pt-3 border-t border-soft">
                <div className="text-app-nano font-semibold uppercase tracking-wider text-muted mb-1">
                  Narrative · v{data.narrative.version}
                </div>
                <p className="text-xs text-secondary whitespace-pre-wrap leading-relaxed">
                  {data.narrative.body}
                </p>
              </div>
            )}
            <RegroupPanel pending={pending} busy={regroupBusy} message={regroupMsg}
                          onPropose={() => void propose()} onDecide={(id, d) => void decide(id, d)}
                          tree={tree} />
          </>
        )}
        {footer}
      </div>
    </div>
  )
}

export default LifecoachLifeMap
